import { t } from "@lingui/macro";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PublishIcon from "@mui/icons-material/Publish";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Stack,
  Step,
  Stepper,
  StepLabel,
  Grid,
} from "@mui/material";
import { AlertColor } from "@mui/material/Alert";
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Alerts from "@/client/components/alerts";
import { useUser } from "@/client/services/user";

const CreateEditSelfInterview = () => {
  const { id, step: stepParam } = useParams<{ id: string; step: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const studentId = user?.id;

  const [currentStep, setCurrentStep] = useState(0);
  const [interviewId, setInterviewId] = useState<string | null>(id || null);
  const [interview, setInterview] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    duration: 10,
  });
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const isEditMode = !!id;
  const steps = [t`Interview Details`, t`Add Questions`, t`Publish`];

  useEffect(() => {
    if (stepParam) {
      setCurrentStep(parseInt(stepParam) || 0);
    }
    if (id) {
      fetchInterview();
    }
  }, [id, stepParam]);

  const fetchInterview = async () => {
    try {
      const res = await axios.get(
        `/api/integration/external-service/self-interview/${id}`,
        {
          params: { studentId },
        }
      );
      const interviewData = res.data.result;
      setInterview(interviewData);
      setInterviewId(interviewData._id);
      setFormData({
        name: interviewData.name || "",
        description: interviewData.description || "",
        category: interviewData.category || "",
        duration: interviewData.duration || 10,
      });
    } catch (error) {
      console.error("Error fetching interview:", error);
      setAlert({
        open: true,
        message: t`Failed to fetch interview details`,
        severity: "error",
      });
    }
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateInterview = async () => {
    if (!formData.name.trim()) {
      setAlert({
        open: true,
        message: t`Interview name is required`,
        severity: "warning",
      });
      return;
    }
    if (!formData.category) {
      setAlert({
        open: true,
        message: t`Interview category is required`,
        severity: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        duration: Number(formData.duration),
        studentId,
      };

      if (isEditMode && id) {
        await axios.patch(
          `/api/integration/external-service/self-interview/${id}`,
          payload,
          {
            params: { studentId },
          }
        );
        setAlert({
          open: true,
          message: t`Interview updated successfully`,
          severity: "success",
        });
        setTimeout(() => {
          navigate(`/self-interviews/create/${id}/1`);
          setCurrentStep(1);
        }, 500);
        return;
      } else {
        const res = await axios.post(
          `/api/integration/external-service/self-interview`,
          payload
        );
        const newId = res.data.result._id;
        setInterviewId(newId);
        setAlert({
          open: true,
          message: t`Interview created successfully`,
          severity: "success",
        });
        setTimeout(() => {
          navigate(`/self-interviews/create/${newId}/1`);
          setCurrentStep(1);
        }, 500);
        return;
      }
    } catch (error: any) {
      console.error("Error saving interview:", error);
      setAlert({
        open: true,
        message: error.response?.data?.error || t`Failed to save interview`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNextStep = () => {
    if (currentStep === 0) {
      handleCreateInterview();
    } else if (currentStep === 1) {
      navigate(`/self-interviews/create/${interviewId}/2`);
      setCurrentStep(2);
    }
  };

  const handleSaveAsDraft = async () => {
    setLoading(true);
    try {
      const payload = {
        ...formData,
        duration: Number(formData.duration),
        studentId,
      };

      await axios.patch(
        `/api/integration/external-service/self-interview/${interviewId}`,
        payload,
        {
          params: { studentId },
        }
      );
      setAlert({
        open: true,
        message: t`Interview saved as draft`,
        severity: "success",
      });
      setTimeout(() => {
        navigate("/self-interviews");
      }, 1500);
    } catch (error: any) {
      console.error("Error saving interview:", error);
      setAlert({
        open: true,
        message: error.response?.data?.error || t`Failed to save interview`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    setLoading(true);
    try {
      const payload = {
        ...formData,
        duration: Number(formData.duration),
        studentId,
        status: "published",
      };

      await axios.patch(
        `/api/integration/external-service/self-interview/${interviewId}`,
        payload,
        {
          params: { studentId },
        }
      );
      setAlert({
        open: true,
        message: t`Interview published successfully`,
        severity: "success",
      });
      setTimeout(() => {
        navigate("/self-interviews");
      }, 1500);
    } catch (error: any) {
      console.error("Error publishing interview:", error);
      setAlert({
        open: true,
        message: error.response?.data?.error || t`Failed to publish interview`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      navigate(`/self-interviews/create/${interviewId}/${prevStep}`);
      setCurrentStep(prevStep);
    } else {
      navigate("/self-interviews");
    }
  };

  const handleCloseAlert = () => {
    setAlert({ open: false, message: "", severity: "info" });
  };

  return (
    <Box>
      <Alerts
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />
      <Container maxWidth="md">
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/self-interviews")}
            sx={{ textTransform: "none" }}
          >
            {t`Back to Self-Interviews`}
          </Button>
        </Box>

        <Stepper activeStep={currentStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Card>
          <CardContent sx={{ p: 4 }}>
            {currentStep === 0 && (
              <>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
                  {isEditMode ? t`Edit Interview Details` : t`Create Interview`}
                </Typography>
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t`Interview Name`}
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder={t`e.g., Frontend Developer Interview Practice`}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: 48,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#ccc !important",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#999 !important",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: "#555 !important",
                        },
                      "& .MuiOutlinedInput-root.Mui-focused": {
                        boxShadow: "none !important",
                        outline: "none !important",
                      },
                      "& input:focus": {
                        outline: "none !important",
                        boxShadow: "none !important",
                      },
                    }}
                  />

                  <FormControl fullWidth size="small" required>
                    <InputLabel>{t`Interview Category`}</InputLabel>
                    <Select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      label={t`Interview Category`}
                    >
                      <MenuItem value="practice">{t`Practice Test`}</MenuItem>
                      <MenuItem value="final">{t`Final Test`}</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    size="small"
                    label={t`Total Time Limit (minutes)`}
                    name="duration"
                    type="number"
                    value={formData.duration}
                    onChange={handleChange}
                    inputProps={{ min: 1 }}
                    required
                    sx={{
                      "& .MuiInputBase-root": {
                        height: 45,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#ccc !important",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#999 !important",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: "#555 !important",
                        },
                      "& .MuiOutlinedInput-root.Mui-focused": {
                        boxShadow: "none !important",
                        outline: "none !important",
                      },
                      "& input:focus": {
                        outline: "none !important",
                        boxShadow: "none !important",
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    size="small"
                    label={t`Description`}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    placeholder={t`Brief description of what this interview covers`}
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": {
                          borderColor: "#999",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#555",
                        },
                      },
                      "& .MuiInputBase-inputMultiline:focus": {
                        outline: "none !important",
                        boxShadow: "none !important",
                        border: "none !important",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused textarea": {
                        outline: "none !important",
                      },
                    }}
                  />
                </Stack>
              </>
            )}

            {currentStep === 1 && (
              <>
                {isEditMode && interview?.questions?.length > 0 && (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "17px" }}>
                        {interview?.questions?.length} {` Questions`}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ background: "white", textTransform: "none" }}
                        onClick={() => navigate(`/self-interviews/questions/${interviewId}`)}
                      >
                        {`Manage Questions`}
                      </Button>
                    </Box>

                    <CardContent>
                      <Stack spacing={1} sx={{ maxHeight: 300, overflow: "auto" }}>
                        {interview?.questions?.map((question: any, index: number) => (
                          <Box
                            key={question._id}
                            sx={{
                              px: 2,
                              py: 1.5,
                              bgcolor: "white",
                              borderRadius: 1,
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <Typography variant="body2">
                              {index + 1}. {question.question?.text || ""}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </CardContent>
                  </>
                )}

                {!isEditMode || !interview?.questions?.length ? (
                  <Box
                    sx={{
                      p: 4,
                      bgcolor: "#f5f5f5",
                      borderRadius: 1,
                      textAlign: "center",
                      border: "2px dashed #ccc",
                    }}
                  >
                    <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
                      {isEditMode
                        ? `Add questions to enhance your interview`
                        : `Click the button below to start adding questions`}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{ textTransform: "none" }}
                      onClick={() => navigate(`/self-interviews/questions/${interviewId}`)}
                    >
                      {`Add Questions`}
                    </Button>
                  </Box>
                ) : null}
              </>
            )}

            {currentStep === 2 && (
              <>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
                  {t`Review and Publish`}
                </Typography>
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t`Interview Name`}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body1">{formData.name}</Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t`Category`}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body1">{formData.category}</Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t`Time Limit`}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body1">
                          {formData.duration} {t`minutes`}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t`Description`}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body1">{formData.description}</Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t`Questions Added`}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body1">{interview?.questions?.length || 0}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </>
            )}

            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 4 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={loading}
                sx={{ textTransform: "none" }}
              >
                {currentStep === 0 ? t`Cancel` : t`Back`}
              </Button>

              {currentStep === 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNextStep}
                  disabled={loading || !formData.name.trim()}
                  sx={{ textTransform: "none" }}
                >
                  {isEditMode ? t`Update and Next` : t`Next`}
                </Button>
              )}

              {currentStep === 1 && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNextStep}
                  disabled={loading}
                  sx={{ textTransform: "none" }}
                >
                  {t`Next`}
                </Button>
              )}

              {currentStep === 2 && (
                <>
                  <Button
                    variant="outlined"
                    onClick={handleSaveAsDraft}
                    disabled={loading}
                    sx={{ textTransform: "none" }}
                  >
                    {t`Save as Draft`}
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<PublishIcon />}
                    onClick={handlePublish}
                    disabled={loading}
                    sx={{ textTransform: "none" }}
                  >
                    {t`Publish`}
                  </Button>
                </>
              )}
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default CreateEditSelfInterview;